import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Alert,
  Stack,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchRoundedIcon      from '@mui/icons-material/SearchRounded';
import FilterListRoundedIcon  from '@mui/icons-material/FilterListRounded';
import RefreshRoundedIcon     from '@mui/icons-material/RefreshRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SecurityRoundedIcon    from '@mui/icons-material/SecurityRounded';
import PlagiarismTable from '../../components/plagiarism/PlagiarismTable';
import { getWinnowingAnalytics } from '../../services/winnowingApi';
import { ctListBatches } from '../../services/api';
import {
  DANGER,
  WARNING,
  INDIGO,
  SURFACE_LIGHT,
  TOOLBAR_BG_DARK,
  TOOLBAR_BG_LIGHT,
} from '../../utils/colors';

// ── Summary stat box ──────────────────────────────────────────────────────────

function SummaryStat({ label, value, accent, icon }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        px: 2.5,
        py: 1.75,
        borderRadius: 2,
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : `${accent}10`,
        border: `1.5px solid ${accent}30`,
        minWidth: 130,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: accent }}>
        {icon}
        <Typography variant="overline" fontWeight={700} fontSize="0.65rem" color={accent}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={800} sx={{ color: accent, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PlagiarismAnalyticsPage() {
  // ── Batch list ─────────────────────────────────────────────────────────────
  const [batches,     setBatches]     = useState([]);

  useEffect(() => {
    ctListBatches()
      .then(({ data }) => setBatches(data.batches ?? []))
      .catch(() => {});
  }, []);

  // ── Table state ────────────────────────────────────────────────────────────
  const [rows,        setRows]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  // ── Pagination & sorting ───────────────────────────────────────────────────
  const [page,        setPage]        = useState(0);   // 0-based (MUI)
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy,      setSortBy]      = useState('checkedAt');
  const [sortDir,     setSortDir]     = useState('desc');

  // ── Filters ────────────────────────────────────────────────────────────────
  const [searchStudent,  setSearchStudent]  = useState('');
  const [problemSlug,    setProblemSlug]    = useState('');
  const [flaggedOnly,    setFlaggedOnly]    = useState(false);
  const [batchId,        setBatchId]        = useState('');
  const [activeStudent,  setActiveStudent]  = useState('');
  const [activeProblem,  setActiveProblem]  = useState('');
  const [activeBatch,    setActiveBatch]    = useState('');

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);

    getWinnowingAnalytics({
      page:        page + 1,     // API is 1-based
      limit:       rowsPerPage,
      student:     activeStudent  || undefined,
      problemSlug: activeProblem  || undefined,
      flagged:     flaggedOnly    || undefined,
      batchId:     activeBatch    || undefined,
      sortBy,
      sortDir,
    })
      .then(({ data }) => {
        setRows(data.results || []);
        setTotal(data.total   || 0);
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || 'Failed to load analytics data.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, sortBy, sortDir, activeStudent, activeProblem, flaggedOnly, activeBatch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Summary counts ─────────────────────────────────────────────────────────
  const highSimilarityCount = rows.filter((r) => (r.winnowingSimilarity ?? 0) > 70).length;
  const highRiskCount       = rows.filter((r) => (r.aiRiskScore         ?? 0) > 60).length;
  const flaggedCount        = rows.filter((r) => r.flagged).length;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setPage(0);
    setActiveStudent(searchStudent.trim());
    setActiveProblem(problemSlug.trim());
    setActiveBatch(batchId);
  };

  const handleClearFilters = () => {
    setSearchStudent('');
    setProblemSlug('');
    setFlaggedOnly(false);
    setBatchId('');
    setActiveStudent('');
    setActiveProblem('');
    setActiveBatch('');
    setPage(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApplyFilters();
  };

  const handleSortChange = (field) => {
    setSortDir((prev) =>
      sortBy === field ? (prev === 'desc' ? 'asc' : 'desc') : 'desc'
    );
    setSortBy(field);
    setPage(0);
  };

  const handlePageChange       = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const filtersActive = !!(activeStudent || activeProblem || flaggedOnly || activeBatch);

  return (
    <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, md: 3 }, py: 3 }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? TOOLBAR_BG_DARK : TOOLBAR_BG_LIGHT,
          border: `1px solid ${INDIGO}30`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SecurityRoundedIcon sx={{ color: INDIGO, fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ color: INDIGO, lineHeight: 1.2 }}>
              Plagiarism Analytics
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Powered by Winnowing fingerprint detection
            </Typography>
          </Box>
          {filtersActive && (
            <Chip
              label="Filtered"
              size="small"
              sx={{
                bgcolor: `${INDIGO}18`,
                color: INDIGO,
                fontWeight: 700,
                fontSize: '0.68rem',
                ml: 1,
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Summary stats */}
          {!loading && rows.length > 0 && (
            <>
              <SummaryStat
                label="High Similarity"
                value={highSimilarityCount}
                accent={DANGER}
                icon={<WarningAmberRoundedIcon sx={{ fontSize: 14 }} />}
              />
              <SummaryStat
                label="High AI Risk"
                value={highRiskCount}
                accent={WARNING}
                icon={<WarningAmberRoundedIcon sx={{ fontSize: 14 }} />}
              />
              <SummaryStat
                label="Flagged"
                value={flaggedCount}
                accent={INDIGO}
                icon={<SecurityRoundedIcon sx={{ fontSize: 14 }} />}
              />
            </>
          )}

          <Tooltip title="Refresh">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchData}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} /> : <RefreshRoundedIcon />}
                sx={{ borderColor: `${INDIGO}60`, color: INDIGO, textTransform: 'none' }}
              >
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : SURFACE_LIGHT,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <FilterListRoundedIcon sx={{ color: 'text.secondary', fontSize: 18 }} />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Batch</InputLabel>
          <Select
            value={batchId}
            label="Batch"
            onChange={(e) => setBatchId(e.target.value)}
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="">All Batches</MenuItem>
            {batches.map((b) => (
              <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search student…"
          value={searchStudent}
          onChange={(e) => setSearchStudent(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 180 }}
        />

        <TextField
          size="small"
          placeholder="Problem slug…"
          value={problemSlug}
          onChange={(e) => setProblemSlug(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ width: 180 }}
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={flaggedOnly}
              onChange={(e) => {
                setFlaggedOnly(e.target.checked);
                setPage(0);
              }}
              sx={{
                '& .MuiSwitch-thumb': { bgcolor: DANGER },
                '& .MuiSwitch-track': { bgcolor: `${DANGER}40` },
              }}
            />
          }
          label={
            <Typography variant="body2" fontWeight={600} color={flaggedOnly ? DANGER : 'text.secondary'}>
              Flagged only
            </Typography>
          }
        />

        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          {filtersActive && (
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Clear
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              bgcolor: INDIGO,
              '&:hover': { bgcolor: '#4338ca' },
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Apply
          </Button>
        </Stack>
      </Box>

      {/* ── Active filter chips ──────────────────────────────────────────── */}
      {filtersActive && (
        <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {activeStudent && (
            <Chip
              label={`Student: ${activeStudent}`}
              size="small"
              onDelete={() => { setActiveStudent(''); setSearchStudent(''); setPage(0); }}
              sx={{ fontSize: '0.72rem' }}
            />
          )}
          {activeProblem && (
            <Chip
              label={`Problem: ${activeProblem}`}
              size="small"
              onDelete={() => { setActiveProblem(''); setProblemSlug(''); setPage(0); }}
              sx={{ fontSize: '0.72rem' }}
            />
          )}
          {activeBatch && (
            <Chip
              label={`Batch: ${batches.find((b) => b._id === activeBatch)?.name ?? activeBatch}`}
              size="small"
              onDelete={() => { setActiveBatch(''); setBatchId(''); setPage(0); }}
              sx={{ fontSize: '0.72rem' }}
            />
          )}
          {flaggedOnly && (
            <Chip
              label="Flagged only"
              size="small"
              onDelete={() => { setFlaggedOnly(false); setPage(0); }}
              sx={{ fontSize: '0.72rem', bgcolor: `${DANGER}18`, color: DANGER }}
            />
          )}
        </Box>
      )}

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Total count label ────────────────────────────────────────────── */}
      {!loading && !error && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {total === 0
            ? 'No results.'
            : `Showing ${rows.length} of ${total} result${total !== 1 ? 's' : ''}`}
        </Typography>
      )}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <PlagiarismTable
        rows={rows}
        total={total}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortDir={sortDir}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
      />
    </Container>
  );
}
